import { useForm } from '@/libs/antd/form';
import {
	DeleteOutlined,
	EditOutlined,
	LoadingOutlined,
} from '@ant-design/icons';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import rule from '@/libs/antd/rules';
import {
	getNAPRiskMitigationDetail,
	NAPDetail,
	NAPRiskMitigationPayload,
	NAPState,
	submitNAPRiskMitigation,
} from '@/libs/fleet-api/services/nap';
import { Button, Col, Form, Row, Table } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { FC, Fragment } from 'react';
import RiskMitigationModal, {
	useRiskMitigationModal,
} from './modals/RiskMitigationModal';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useModals } from '@/components/modals/ModalProvider';
import { useUserData } from '@/libs/fleet-api/hooks';

const NAPRiskMitigation: FC<{
	plafonType: NAPDetail['plafonType'];
	napState?: NAPState;
	prospectId: string;
	onSubmitted: () => void;
}> = ({ plafonType, napState, prospectId, onSubmitted }) => {
	const { modal } = useModals();
	const getNAPRiskMitigationQuery = useQuery({
		queryKey: ['getNAPRiskMitigationDetail', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getNAPRiskMitigationDetail(id);
		},
	});

	const { napId, riskAndMitigations, verificationNotes } =
		getNAPRiskMitigationQuery.data?.data ?? {};

	const { form, value, fieldName } = useForm<NAPRiskMitigationPayload>();
	const riskMitigationModal = useRiskMitigationModal();

	const submitNAPRiskMitigationMutation = useMutation({
		mutationFn: submitNAPRiskMitigation,
	});

	const isReadOnly =
		napState?.action === 'VIEW' || napState?.action === 'DISABLED';

	const { groupName } = useUserData() ?? {};

	if (getNAPRiskMitigationQuery.isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Fragment>
			<RiskMitigationModal {...riskMitigationModal} />
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				labelCol={{ span: 4 }}
				wrapperCol={{ span: 20 }}
				onFinish={async (values) => {
					const res = await submitNAPRiskMitigationMutation.mutateAsync(values);
					if (res.data.napNo) {
						await modal.success({
							title: (
								<strong style={{ fontSize: 20 }}>
									Berikut adalah NAP ID Anda
								</strong>
							),
							subTitle: (
								<strong style={{ fontSize: 24, color: '#1557BC' }}>
									{res.data.napNo}
								</strong>
							),
							cancelButtonProps: {
								hidden: false,
							},
							cancelText: 'BACK',
							okText: 'NEXT',
						});
					}
					onSubmitted();
				}}
			>
				<Form.Item
					name={fieldName('napId')}
					initialValue={napId}
					noStyle
				/>
				<Row style={{ marginBottom: 20 }}>
					<Col flex="auto">
						{plafonType === 'NON_JOIN_FINANCING' && (
							<strong>
								<i>
									Diisi hal-hal yang menjadi risiko pembiayaan dan penjabaran
									mitigasi
								</i>
							</strong>
						)}
					</Col>
					<Col flex="none">
						<Button
							style={{
								borderRadius: 50,
								fontSize: 12,
								height: 24,
								fontWeight: 700,
								...(!isReadOnly && {
									borderColor: '#1557BC',
									color: '#1557BC',
								}),
							}}
							icon={'+'}
							disabled={isReadOnly}
							onClick={async () => {
								if (!isReadOnly) {
									const newData = await riskMitigationModal.open();
									if (!newData) return;

									form.setFieldValue(fieldName('riskAndMitigations'), [
										...(value?.riskAndMitigations ?? []),
										newData,
									]);
								}
							}}
						>
							Add Another
						</Button>
					</Col>
				</Row>

				<Form.Item
					name={fieldName('riskAndMitigations')}
					initialValue={riskAndMitigations ?? []}
					noStyle
				/>

				<Table
					bordered
					pagination={false}
					dataSource={value?.riskAndMitigations?.map((data, i) => {
						return {
							...data,
							key: i + 1,
						};
					})}
					columns={[
						{
							hidden: groupName === 'CCA',
							title: 'No',
							align: 'center',
							width: 80,
							render: (_, __, index) => index + 1,
						},
						{
							hidden: groupName !== 'CCA',
							title: 'Action',
							colSpan: 2,
							width: 100,
							align: 'center',
							render: (_, record, index) => (
								<a
									style={isReadOnly ? { color: '#bababa' } : {}}
									onClick={async (e) => {
										e.preventDefault();
										if (!isReadOnly) {
											const editData = await riskMitigationModal.open(record);
											if (!editData) return;

											form.setFieldValue(
												fieldName('riskAndMitigations'),
												value?.riskAndMitigations?.map((data, dataIndex) => {
													return index === dataIndex ? editData : data;
												}),
											);
										}
									}}
								>
									<EditOutlined />
								</a>
							),
						},
						{
							hidden: groupName !== 'CCA',
							colSpan: 0,
							width: 100,
							align: 'center',
							render: (_, __, index) => (
								<a
									style={isReadOnly ? { color: '#bababa' } : {}}
									onClick={(e) => {
										e.preventDefault();
										if (!isReadOnly) {
											form.setFieldValue(
												fieldName('riskAndMitigations'),
												value?.riskAndMitigations?.filter(
													(_, i) => index !== i,
												),
											);
										}
									}}
								>
									<DeleteOutlined />
								</a>
							),
						},
						{
							title: 'Risiko',
							render: (_, record) => record?.risk,
						},
						{
							title: 'Mitigasi',
							render: (_, record) => record?.mitigation,
						},
					]}
				/>

				<Form.Item
					label={groupName === 'CCA' ? 'Informasi Tambahan' : 'Notes CCA'}
					style={{ marginTop: 20 }}
					name={fieldName('verificationNotes')}
					rules={[rule.required()]}
					initialValue={verificationNotes}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>

				{groupName === 'CCA' && (
					<Button
						type="primary"
						htmlType="submit"
						style={{
							width: 200,
							display: 'block',
							marginLeft: 'auto',
							marginTop: 40,
						}}
						disabled={submitNAPRiskMitigationMutation.isPending || isReadOnly}
						loading={submitNAPRiskMitigationMutation.isPending}
					>
						SAVE
					</Button>
				)}
			</Form>
		</Fragment>
	);
};

export default NAPRiskMitigation;
